import { useMemo } from "react";
import { create } from "zustand";
import { CATEGORIES as BASE_CATEGORIES } from "@/content/categories";
import { CHALLENGE_TEMPLATES as BASE_CHALLENGES } from "@/content/challenges";
import { TRIAGE_LENSES as BASE_LENSES, type TriageLens } from "@/content/triage";
import { FACET_ORDER as BASE_AXIS_ORDER, FACET_LABELS as BASE_AXIS_LABELS, FACET_POOLS as BASE_AXIS_POOLS } from "@/content/facets";
import {
  INTERVIEW_ROLES as BASE_ROLES,
  INTERVIEW_TYPES as BASE_TYPES,
  INTERVIEW_QUESTIONS as BASE_QUESTIONS,
  type InterviewRole,
  type InterviewType,
  type InterviewQuestion,
} from "@/content/interview";
import type { Category, ChallengeTemplate } from "@/core/data/types";

/**
 * Overlay store for the in-app Content Studio. User additions and removals are
 * layered on top of the built-in seeds and persisted locally — built-ins are
 * *hidden* rather than hard-deleted (so referencing content never breaks), while
 * user-added items are fully deletable. First cluster: Disciplines.
 */

const STORAGE_KEY = "hippo.customContent";

interface Persisted {
  addedDisciplines: Category[];
  hiddenDisciplineIds: string[];
  addedRoles: InterviewRole[];
  hiddenRoleIds: string[];
  addedTypes: InterviewType[];
  hiddenTypeIds: string[];
  addedChallenges: ChallengeTemplate[];
  hiddenChallengeIds: string[];
  challengeOverrides: Record<string, ChallengeTemplate>;
  addedQuestions: InterviewQuestion[];
  hiddenQuestionIds: string[];
  questionOverrides: Record<string, InterviewQuestion>;
  addedAxes: { key: string; label: string }[];
  hiddenAxisKeys: string[];
  addedValues: Record<string, string[]>;
  hiddenValues: Record<string, string[]>;
  tags: string[];
  addedLenses: TriageLens[];
  hiddenLensIds: string[];
  lensOverrides: Record<string, TriageLens>;
}

const EMPTY: Persisted = {
  addedDisciplines: [],
  hiddenDisciplineIds: [],
  addedRoles: [],
  hiddenRoleIds: [],
  addedTypes: [],
  hiddenTypeIds: [],
  addedChallenges: [],
  hiddenChallengeIds: [],
  challengeOverrides: {},
  addedQuestions: [],
  hiddenQuestionIds: [],
  questionOverrides: {},
  addedAxes: [],
  hiddenAxisKeys: [],
  addedValues: {},
  hiddenValues: {},
  tags: [],
  addedLenses: [],
  hiddenLensIds: [],
  lensOverrides: {},
};

export interface ConceptAxis {
  key: string;
  label: string;
  values: string[];
}

function buildConceptAxes(p: Pick<Persisted, "addedAxes" | "hiddenAxisKeys" | "addedValues" | "hiddenValues">): ConceptAxis[] {
  const hiddenAxes = new Set(p.hiddenAxisKeys);
  const base: ConceptAxis[] = BASE_AXIS_ORDER.filter((k) => !hiddenAxes.has(k)).map((k) => {
    const hide = new Set(p.hiddenValues[k] ?? []);
    return {
      key: k,
      label: BASE_AXIS_LABELS[k],
      values: [...BASE_AXIS_POOLS[k].filter((v) => !hide.has(v)), ...(p.addedValues[k] ?? [])],
    };
  });
  const custom: ConceptAxis[] = p.addedAxes
    .filter((a) => !hiddenAxes.has(a.key))
    .map((a) => ({ key: a.key, label: a.label, values: p.addedValues[a.key] ?? [] }));
  return [...base, ...custom];
}

function load(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Persisted>;
      return { ...EMPTY, ...p };
    }
  } catch {
    /* ignore */
  }
  return { ...EMPTY };
}

export function slugId(prefix: string, name: string): string {
  const base = name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return `${prefix}-${base || Math.random().toString(36).slice(2, 8)}`;
}

interface State extends Persisted {
  effectiveCategories: () => Category[];
  isHiddenDiscipline: (id: string) => boolean;
  isCustomDiscipline: (id: string) => boolean;
  addDiscipline: (c: { id?: string; name: string; group: string; description?: string; enabledByDefault?: boolean }) => void;
  updateDiscipline: (c: Category) => void;
  removeAddedDiscipline: (id: string) => void;
  toggleHiddenDiscipline: (id: string) => void;

  effectiveRoles: () => InterviewRole[];
  effectiveTypes: () => InterviewType[];
  isHiddenRole: (id: string) => boolean;
  isCustomRole: (id: string) => boolean;
  isHiddenType: (id: string) => boolean;
  isCustomType: (id: string) => boolean;
  addRole: (r: { name: string; description?: string }) => void;
  removeAddedRole: (id: string) => void;
  toggleHiddenRole: (id: string) => void;
  addType: (t: { name: string; description?: string }) => void;
  removeAddedType: (id: string) => void;
  toggleHiddenType: (id: string) => void;

  effectiveChallenges: () => ChallengeTemplate[];
  isHiddenChallenge: (id: string) => boolean;
  isCustomChallenge: (id: string) => boolean;
  isEditedChallenge: (id: string) => boolean;
  saveChallenge: (t: ChallengeTemplate) => void;
  removeChallenge: (id: string) => void;
  revertChallenge: (id: string) => void;

  effectiveQuestions: () => InterviewQuestion[];
  isHiddenQuestion: (id: string) => boolean;
  isCustomQuestion: (id: string) => boolean;
  isEditedQuestion: (id: string) => boolean;
  saveQuestion: (qn: InterviewQuestion) => void;
  removeQuestion: (id: string) => void;
  revertQuestion: (id: string) => void;

  conceptAxes: () => ConceptAxis[];
  isCustomAxis: (key: string) => boolean;
  isHiddenAxis: (key: string) => boolean;
  addAxis: (label: string) => void;
  removeAxis: (key: string) => void;
  toggleHiddenAxis: (key: string) => void;
  addValue: (key: string, value: string) => void;
  removeValue: (key: string, value: string) => void;

  addTag: (tag: string) => void;
  removeTag: (tag: string) => void;

  effectiveLenses: () => TriageLens[];
  isHiddenLens: (id: string) => boolean;
  isCustomLens: (id: string) => boolean;
  isEditedLens: (id: string) => boolean;
  saveLens: (lens: TriageLens) => void;
  removeLens: (id: string) => void;
  revertLens: (id: string) => void;
}

function effective<T extends { id: string }>(base: T[], added: T[], hidden: string[]): T[] {
  const hide = new Set(hidden);
  return [...base.filter((c) => !hide.has(c.id)), ...added];
}

/** Like effective(), but with per-id overrides applied to the base seeds. */
function overlay<T extends { id: string }>(
  base: T[],
  added: T[],
  hidden: string[],
  overrides: Record<string, T>
): T[] {
  const hide = new Set(hidden);
  return [...base.filter((c) => !hide.has(c.id)).map((c) => overrides[c.id] ?? c), ...added];
}

function baseHasId<T extends { id: string }>(base: T[], id: string): boolean {
  return base.some((c) => c.id === id);
}

export const useCustomContentStore = create<State>((set, get) => {
  const persist = () => {
    const s = get();
    const data: Persisted = {
      addedDisciplines: s.addedDisciplines,
      hiddenDisciplineIds: s.hiddenDisciplineIds,
      addedRoles: s.addedRoles,
      hiddenRoleIds: s.hiddenRoleIds,
      addedTypes: s.addedTypes,
      hiddenTypeIds: s.hiddenTypeIds,
      addedChallenges: s.addedChallenges,
      hiddenChallengeIds: s.hiddenChallengeIds,
      challengeOverrides: s.challengeOverrides,
      addedQuestions: s.addedQuestions,
      hiddenQuestionIds: s.hiddenQuestionIds,
      questionOverrides: s.questionOverrides,
      addedAxes: s.addedAxes,
      hiddenAxisKeys: s.hiddenAxisKeys,
      addedValues: s.addedValues,
      hiddenValues: s.hiddenValues,
      tags: s.tags,
      addedLenses: s.addedLenses,
      hiddenLensIds: s.hiddenLensIds,
      lensOverrides: s.lensOverrides,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  };

  return {
    ...load(),

    effectiveCategories: () => effective(BASE_CATEGORIES, get().addedDisciplines, get().hiddenDisciplineIds),
    isHiddenDiscipline: (id) => get().hiddenDisciplineIds.includes(id),
    isCustomDiscipline: (id) => get().addedDisciplines.some((c) => c.id === id),

    addDiscipline: (c) => {
      const id = c.id ?? slugId("disc", c.name);
      const cat: Category = {
        id,
        name: c.name.trim() || "New discipline",
        group: c.group || "Design",
        description: c.description ?? "",
        enabledByDefault: c.enabledByDefault ?? false,
      };
      set({ addedDisciplines: [...get().addedDisciplines.filter((d) => d.id !== id), cat] });
      persist();
    },
    updateDiscipline: (c) => {
      set({ addedDisciplines: get().addedDisciplines.map((d) => (d.id === c.id ? c : d)) });
      persist();
    },
    removeAddedDiscipline: (id) => {
      set({ addedDisciplines: get().addedDisciplines.filter((d) => d.id !== id) });
      persist();
    },
    toggleHiddenDiscipline: (id) => {
      const hidden = get().hiddenDisciplineIds;
      set({
        hiddenDisciplineIds: hidden.includes(id) ? hidden.filter((x) => x !== id) : [...hidden, id],
      });
      persist();
    },

    // ---- Interview roles ----
    effectiveRoles: () => effective(BASE_ROLES, get().addedRoles, get().hiddenRoleIds),
    isHiddenRole: (id) => get().hiddenRoleIds.includes(id),
    isCustomRole: (id) => get().addedRoles.some((r) => r.id === id),
    addRole: (r) => {
      const role: InterviewRole = {
        id: slugId("role", r.name),
        name: r.name.trim() || "New role",
        description: r.description ?? "",
        enabledByDefault: false,
      };
      set({ addedRoles: [...get().addedRoles, role] });
      persist();
    },
    removeAddedRole: (id) => {
      set({ addedRoles: get().addedRoles.filter((r) => r.id !== id) });
      persist();
    },
    toggleHiddenRole: (id) => {
      const h = get().hiddenRoleIds;
      set({ hiddenRoleIds: h.includes(id) ? h.filter((x) => x !== id) : [...h, id] });
      persist();
    },

    // ---- Interview question types ----
    effectiveTypes: () => effective(BASE_TYPES, get().addedTypes, get().hiddenTypeIds),
    isHiddenType: (id) => get().hiddenTypeIds.includes(id),
    isCustomType: (id) => get().addedTypes.some((t) => t.id === id),
    addType: (t) => {
      const type: InterviewType = {
        id: slugId("itype", t.name),
        name: t.name.trim() || "New type",
        description: t.description ?? "",
      };
      set({ addedTypes: [...get().addedTypes, type] });
      persist();
    },
    removeAddedType: (id) => {
      set({ addedTypes: get().addedTypes.filter((t) => t.id !== id) });
      persist();
    },
    toggleHiddenType: (id) => {
      const h = get().hiddenTypeIds;
      set({ hiddenTypeIds: h.includes(id) ? h.filter((x) => x !== id) : [...h, id] });
      persist();
    },

    // ---- Challenge templates ----
    effectiveChallenges: () =>
      overlay(BASE_CHALLENGES, get().addedChallenges, get().hiddenChallengeIds, get().challengeOverrides),
    isHiddenChallenge: (id) => get().hiddenChallengeIds.includes(id),
    isCustomChallenge: (id) => get().addedChallenges.some((c) => c.id === id),
    isEditedChallenge: (id) => Boolean(get().challengeOverrides[id]),
    saveChallenge: (t) => {
      if (baseHasId(BASE_CHALLENGES, t.id)) {
        set({ challengeOverrides: { ...get().challengeOverrides, [t.id]: t } });
      } else {
        const exists = get().addedChallenges.some((c) => c.id === t.id);
        set({
          addedChallenges: exists
            ? get().addedChallenges.map((c) => (c.id === t.id ? t : c))
            : [t, ...get().addedChallenges],
        });
      }
      persist();
    },
    removeChallenge: (id) => {
      if (baseHasId(BASE_CHALLENGES, id)) {
        const h = get().hiddenChallengeIds;
        set({ hiddenChallengeIds: h.includes(id) ? h : [...h, id] });
      } else {
        set({ addedChallenges: get().addedChallenges.filter((c) => c.id !== id) });
      }
      persist();
    },
    revertChallenge: (id) => {
      const overrides = { ...get().challengeOverrides };
      delete overrides[id];
      set({ challengeOverrides: overrides, hiddenChallengeIds: get().hiddenChallengeIds.filter((x) => x !== id) });
      persist();
    },

    // ---- Interview questions ----
    effectiveQuestions: () =>
      overlay(BASE_QUESTIONS, get().addedQuestions, get().hiddenQuestionIds, get().questionOverrides),
    isHiddenQuestion: (id) => get().hiddenQuestionIds.includes(id),
    isCustomQuestion: (id) => get().addedQuestions.some((q) => q.id === id),
    isEditedQuestion: (id) => Boolean(get().questionOverrides[id]),
    saveQuestion: (qn) => {
      if (baseHasId(BASE_QUESTIONS, qn.id)) {
        set({ questionOverrides: { ...get().questionOverrides, [qn.id]: qn } });
      } else {
        const exists = get().addedQuestions.some((q) => q.id === qn.id);
        set({
          addedQuestions: exists
            ? get().addedQuestions.map((q) => (q.id === qn.id ? qn : q))
            : [qn, ...get().addedQuestions],
        });
      }
      persist();
    },
    removeQuestion: (id) => {
      if (baseHasId(BASE_QUESTIONS, id)) {
        const h = get().hiddenQuestionIds;
        set({ hiddenQuestionIds: h.includes(id) ? h : [...h, id] });
      } else {
        set({ addedQuestions: get().addedQuestions.filter((q) => q.id !== id) });
      }
      persist();
    },
    revertQuestion: (id) => {
      const overrides = { ...get().questionOverrides };
      delete overrides[id];
      set({ questionOverrides: overrides, hiddenQuestionIds: get().hiddenQuestionIds.filter((x) => x !== id) });
      persist();
    },

    // ---- Game Concepts (facet axes + values) ----
    conceptAxes: () => buildConceptAxes(get()),
    isCustomAxis: (key) => get().addedAxes.some((a) => a.key === key),
    isHiddenAxis: (key) => get().hiddenAxisKeys.includes(key),
    addAxis: (label) => {
      const key = slugId("axis", label);
      set({ addedAxes: [...get().addedAxes, { key, label: label.trim() || "New axis" }] });
      persist();
    },
    removeAxis: (key) => {
      // Custom axis → delete; built-in axis → hide.
      if (get().addedAxes.some((a) => a.key === key)) {
        const addedValues = { ...get().addedValues };
        const hiddenValues = { ...get().hiddenValues };
        delete addedValues[key];
        delete hiddenValues[key];
        set({ addedAxes: get().addedAxes.filter((a) => a.key !== key), addedValues, hiddenValues });
      } else {
        const h = get().hiddenAxisKeys;
        set({ hiddenAxisKeys: h.includes(key) ? h : [...h, key] });
      }
      persist();
    },
    toggleHiddenAxis: (key) => {
      const h = get().hiddenAxisKeys;
      set({ hiddenAxisKeys: h.includes(key) ? h.filter((x) => x !== key) : [...h, key] });
      persist();
    },
    addValue: (key, value) => {
      const v = value.trim();
      if (!v) return;
      const added = { ...get().addedValues };
      const isBase = (BASE_AXIS_POOLS as Record<string, string[]>)[key]?.includes(v);
      // Unhide if it was a hidden base value; otherwise add as a custom value.
      if (isBase) {
        const hiddenValues = { ...get().hiddenValues, [key]: (get().hiddenValues[key] ?? []).filter((x) => x !== v) };
        set({ hiddenValues });
      } else if (!(added[key] ?? []).includes(v)) {
        added[key] = [...(added[key] ?? []), v];
        set({ addedValues: added });
      }
      persist();
    },
    removeValue: (key, value) => {
      const isBase = (BASE_AXIS_POOLS as Record<string, string[]>)[key]?.includes(value);
      if (isBase) {
        const hiddenValues = { ...get().hiddenValues, [key]: [...(get().hiddenValues[key] ?? []), value] };
        set({ hiddenValues });
      } else {
        const added = { ...get().addedValues, [key]: (get().addedValues[key] ?? []).filter((x) => x !== value) };
        set({ addedValues: added });
      }
      persist();
    },

    // ---- Triage lenses ----
    effectiveLenses: () => overlay(BASE_LENSES, get().addedLenses, get().hiddenLensIds, get().lensOverrides),
    isHiddenLens: (id) => get().hiddenLensIds.includes(id),
    isCustomLens: (id) => get().addedLenses.some((l) => l.id === id),
    isEditedLens: (id) => Boolean(get().lensOverrides[id]),
    saveLens: (lens) => {
      if (baseHasId(BASE_LENSES, lens.id)) {
        set({ lensOverrides: { ...get().lensOverrides, [lens.id]: lens } });
      } else {
        const exists = get().addedLenses.some((l) => l.id === lens.id);
        set({
          addedLenses: exists
            ? get().addedLenses.map((l) => (l.id === lens.id ? lens : l))
            : [...get().addedLenses, lens],
        });
      }
      persist();
    },
    removeLens: (id) => {
      if (baseHasId(BASE_LENSES, id)) {
        const h = get().hiddenLensIds;
        set({ hiddenLensIds: h.includes(id) ? h : [...h, id] });
      } else {
        set({ addedLenses: get().addedLenses.filter((l) => l.id !== id) });
      }
      persist();
    },
    revertLens: (id) => {
      const overrides = { ...get().lensOverrides };
      delete overrides[id];
      set({ lensOverrides: overrides, hiddenLensIds: get().hiddenLensIds.filter((x) => x !== id) });
      persist();
    },

    // ---- Tags (managed vocabulary) ----
    addTag: (tag) => {
      const t = tag.trim();
      if (!t || get().tags.includes(t)) return;
      set({ tags: [...get().tags, t] });
      persist();
    },
    removeTag: (tag) => {
      set({ tags: get().tags.filter((t) => t !== tag) });
      persist();
    },
  };
});

// ---- Non-reactive snapshot helpers (for generation/display in non-React code) ----

export function getCategories(): Category[] {
  return useCustomContentStore.getState().effectiveCategories();
}
export function categoryName(id: string): string {
  return getCategories().find((c) => c.id === id)?.name ?? id;
}
export function categoryById(id: string): Category | undefined {
  return getCategories().find((c) => c.id === id);
}
export function getRoles(): InterviewRole[] {
  return useCustomContentStore.getState().effectiveRoles();
}
export function getTypes(): InterviewType[] {
  return useCustomContentStore.getState().effectiveTypes();
}
export function getChallenges(): ChallengeTemplate[] {
  return useCustomContentStore.getState().effectiveChallenges();
}
export function getQuestions(): InterviewQuestion[] {
  return useCustomContentStore.getState().effectiveQuestions();
}
export function getLenses(): TriageLens[] {
  return useCustomContentStore.getState().effectiveLenses();
}
export function getConceptAxes(): ConceptAxis[] {
  return useCustomContentStore.getState().conceptAxes();
}
export function getAxisOrder(): string[] {
  return getConceptAxes().map((a) => a.key);
}
export function getFacetPools(): Record<string, string[]> {
  return Object.fromEntries(getConceptAxes().map((a) => [a.key, a.values]));
}
export function getFacetLabels(): Record<string, string> {
  return Object.fromEntries(getConceptAxes().map((a) => [a.key, a.label]));
}
export function roleName(id: string): string {
  return getRoles().find((r) => r.id === id)?.name ?? id;
}
export function typeName(id: string): string {
  return getTypes().find((t) => t.id === id)?.name ?? id;
}

/** Reactive effective discipline list for components. */
export function useCategories(): Category[] {
  const added = useCustomContentStore((s) => s.addedDisciplines);
  const hidden = useCustomContentStore((s) => s.hiddenDisciplineIds);
  return useMemo(() => effective(BASE_CATEGORIES, added, hidden), [added, hidden]);
}
export function useInterviewRoles(): InterviewRole[] {
  const added = useCustomContentStore((s) => s.addedRoles);
  const hidden = useCustomContentStore((s) => s.hiddenRoleIds);
  return useMemo(() => effective(BASE_ROLES, added, hidden), [added, hidden]);
}
export function useInterviewTypes(): InterviewType[] {
  const added = useCustomContentStore((s) => s.addedTypes);
  const hidden = useCustomContentStore((s) => s.hiddenTypeIds);
  return useMemo(() => effective(BASE_TYPES, added, hidden), [added, hidden]);
}
export function useChallenges(): ChallengeTemplate[] {
  const added = useCustomContentStore((s) => s.addedChallenges);
  const hidden = useCustomContentStore((s) => s.hiddenChallengeIds);
  const overrides = useCustomContentStore((s) => s.challengeOverrides);
  return useMemo(() => overlay(BASE_CHALLENGES, added, hidden, overrides), [added, hidden, overrides]);
}
export function useQuestions(): InterviewQuestion[] {
  const added = useCustomContentStore((s) => s.addedQuestions);
  const hidden = useCustomContentStore((s) => s.hiddenQuestionIds);
  const overrides = useCustomContentStore((s) => s.questionOverrides);
  return useMemo(() => overlay(BASE_QUESTIONS, added, hidden, overrides), [added, hidden, overrides]);
}
export function useLenses(): TriageLens[] {
  const added = useCustomContentStore((s) => s.addedLenses);
  const hidden = useCustomContentStore((s) => s.hiddenLensIds);
  const overrides = useCustomContentStore((s) => s.lensOverrides);
  return useMemo(() => overlay(BASE_LENSES, added, hidden, overrides), [added, hidden, overrides]);
}
export function useConceptAxes(): ConceptAxis[] {
  const addedAxes = useCustomContentStore((s) => s.addedAxes);
  const hiddenAxisKeys = useCustomContentStore((s) => s.hiddenAxisKeys);
  const addedValues = useCustomContentStore((s) => s.addedValues);
  const hiddenValues = useCustomContentStore((s) => s.hiddenValues);
  return useMemo(
    () => buildConceptAxes({ addedAxes, hiddenAxisKeys, addedValues, hiddenValues }),
    [addedAxes, hiddenAxisKeys, addedValues, hiddenValues]
  );
}
